import { AssetType, RenderMode, type AssetRef, type Suite } from "#gen/bundles/v1/bundle_pb.js";
import { Mode } from "#gen/bundles/v1/service_pb.js";
import type { AssetLoad } from "#lib/api/bundles.js";
import type { BundleDag, BundleNode } from "#lib/dag.js";
import { presentsAsFullPage } from "#lib/page.js";

const PASSTHROUGH_ASSET_TYPES: Set<AssetType> = new Set([AssetType.DECISION, AssetType.EXPERIMENT, AssetType.LOOKUP]);

export function isPassthroughType(type: AssetType = AssetType.UNSPECIFIED): boolean {
	return PASSTHROUGH_ASSET_TYPES.has(type);
}

// Keys of the first renderable page reached from each entrypoint, walking
// through passthrough nodes like decision, experiment, and lookup.
export function firstPageKeys(dag: BundleDag<AssetRef>): Set<string> {
	const keys: Set<string> = new Set();
	const seen: Set<string> = new Set();
	const queue: string[] = dag.roots.flatMap(root => root.linksOut.map(link => link.destination));
	while (queue.length > 0) {
		const nodeKey = queue.shift();
		if (!nodeKey || seen.has(nodeKey)) {
			continue;
		}
		seen.add(nodeKey);
		const node = dag.getNode(nodeKey);
		if (!node) {
			continue;
		}
		if (isPassthroughType(node.ref.type)) {
			queue.push(...node.linksOut.map(link => link.destination));
		} else {
			keys.add(nodeKey);
		}
	}
	return keys;
}

export function landsAsFullPage(suite: Suite, firstPages: Set<string>, key: string): boolean {
	const ref = suite.assets[key];
	return ref?.type === AssetType.PAGE_V2 && (firstPages.has(key) || presentsAsFullPage(ref));
}

// Return whether a page is shown full screen rather than in a tray.
export function rendersAsFullPage(dag: BundleDag<AssetRef> | undefined, key: string | undefined, ref: AssetRef | undefined): boolean {
	if (!dag || !key) {
		return false;
	}
	return firstPageKeys(dag).has(key) || presentsAsFullPage(ref);
}

export type BackgroundPaint = "layer" | "unpinned" | "inline" | "none";

interface BackgroundPaintArgs {
	mode: Mode;
	modal?: boolean;
	renderMode?: RenderMode;
	isDesktopUserAgent?: boolean;
	desktopViewEnabled?: boolean;
	fullBleedContainer?: boolean;
}

// The layer is sized in viewport units and sticks to the flow scrollport, so only the live full page
// can use it. Anything drawn in a bounded card instead resolves a fixed attachment against the browser
// window rather than the card, so those surfaces paint the image with the pinning removed.
//
// An inline module keeps the attachment because it inherits the background of the page behind it. That
// inherited copy only stays invisible while it is pinned to the same box as the page it sits on.
export function resolveBackgroundPaint(args: BackgroundPaintArgs): BackgroundPaint {
	if (args.fullBleedContainer) {
		return "none";
	}
	if (args.renderMode === RenderMode.INLINE || args.modal) {
		return "inline";
	}
	if (args.mode !== Mode.LIVE) {
		return "unpinned";
	}
	if (args.isDesktopUserAgent && !args.desktopViewEnabled) {
		return "unpinned";
	}
	return "layer";
}

export function getIconUrl(assets: Record<string, AssetLoad>, moduleKey: string, ref: AssetRef | undefined, mode: Mode): string | undefined {
	if (mode !== Mode.EDITING && assets[moduleKey]?.iconUrl() !== undefined) {
		return assets[moduleKey]?.iconUrl();
	}
	return ref?.iconUrl;
}

export function getText(assets: Record<string, AssetLoad>, moduleKey: string, ref: AssetRef | undefined, mode: Mode): string {
	if (mode !== Mode.EDITING && assets[moduleKey]?.shortName() !== undefined) {
		return assets[moduleKey].shortName() || assets[moduleKey].displayName() || "";
	}
	return ref?.shortName || ref?.displayName || "";
}

export function getShortName(assets: Record<string, AssetLoad>, moduleKey: string, ref: AssetRef | undefined, mode: Mode): string {
	if (mode !== Mode.EDITING && assets[moduleKey]) {
		return assets[moduleKey].shortName();
	}
	return ref?.shortName ?? "";
}

// A module's call-to-action reflects the module (parent) ref, not the nested target asset.
// Resolve the parent's suite-level ref so a module without a custom label falls back to the
// default action label instead of surfacing the inner asset's name.
export function getAssetRef(suite: Suite, node: BundleNode<AssetRef>): AssetRef | undefined {
	if (node.parent) {
		return suite.assets[node.parent];
	}
	return suite.assets[node.key];
}

function fallbackBranchDestination(node: BundleNode<AssetRef>): string | undefined {
	const fallback = node.linksOut.find(link => link.conditions.length === 0);
	return (fallback ?? node.linksOut[0])?.destination;
}

function unwrapModuleNode(dag: BundleDag<AssetRef>, node: BundleNode<AssetRef> | undefined): BundleNode<AssetRef> | undefined {
	if (node && dag.isModuleRoot(node.key)) {
		const moduleKey = dag.getContainingModule(node.key);
		const moduleNode = moduleKey ? dag.getNode(moduleKey) : undefined;
		return moduleNode ?? node;
	}
	return node;
}

export function defaultModuleNode(dag: BundleDag<AssetRef>, node: BundleNode<AssetRef> | undefined): BundleNode<AssetRef> | undefined {
	const seen: Set<string> = new Set();
	let current = node;
	while (current && isPassthroughType(current.ref.type)) {
		if (seen.has(current.key)) {
			return undefined;
		}
		seen.add(current.key);
		const destination = fallbackBranchDestination(current);
		current = destination ? dag.getNode(destination) : undefined;
	}
	return unwrapModuleNode(dag, current);
}

export function resolvedModuleNode(dag: BundleDag<AssetRef>, node: BundleNode<AssetRef> | undefined, resolvedKey: string | undefined): BundleNode<AssetRef> | undefined {
	const resolved = resolvedKey ? dag.getNode(resolvedKey) : undefined;
	return resolved ? unwrapModuleNode(dag, resolved) : defaultModuleNode(dag, node);
}
